import type { StreamingState, StreamEvent, EventHandler } from './types';
import { StreamEventType } from './types';
import {
  handleResponseCreated,
  handleResponseInProgress,
  handleTextDelta,
  handleTextDone,
  handleResponseCompleted,
  handleResponseFailed,
  handleWebSearchInProgress,
  handleWebSearchSearching,
  handleWebSearchCompleted,
  handleReasoningSummaryDelta,
  handleReasoningSummaryDone,
  handleUnknown,
} from './handlers';

// Registry of event handlers
// Using type assertion since specific handlers are subtypes of EventHandler
const eventHandlers: Record<string, EventHandler> = {
  [StreamEventType.RESPONSE_CREATED]: handleResponseCreated as EventHandler,
  [StreamEventType.RESPONSE_IN_PROGRESS]: handleResponseInProgress,
  [StreamEventType.OUTPUT_TEXT_DELTA]: handleTextDelta as EventHandler,
  [StreamEventType.OUTPUT_TEXT_DONE]: handleTextDone as EventHandler,
  [StreamEventType.RESPONSE_COMPLETED]: handleResponseCompleted as EventHandler,
  [StreamEventType.RESPONSE_FAILED]: handleResponseFailed as EventHandler,
  // Items and content parts use default handler (just update sequence number)
  [StreamEventType.OUTPUT_ITEM_ADDED]: handleUnknown,
  [StreamEventType.OUTPUT_ITEM_DONE]: handleUnknown,
  [StreamEventType.CONTENT_PART_ADDED]: handleUnknown,
  [StreamEventType.CONTENT_PART_DONE]: handleUnknown,
  // Web search events
  [StreamEventType.WEB_SEARCH_CALL_IN_PROGRESS]: handleWebSearchInProgress as EventHandler,
  [StreamEventType.WEB_SEARCH_CALL_SEARCHING]: handleWebSearchSearching as EventHandler,
  [StreamEventType.WEB_SEARCH_CALL_COMPLETED]: handleWebSearchCompleted as EventHandler,
  // Reasoning summary events
  [StreamEventType.REASONING_SUMMARY_TEXT_DELTA]: handleReasoningSummaryDelta as EventHandler,
  [StreamEventType.REASONING_SUMMARY_TEXT_DONE]: handleReasoningSummaryDone as EventHandler,
};

/**
 * Pure reducer function for streaming events
 */
export function streamEventReducer(
  state: StreamingState,
  event: StreamEvent
): StreamingState {
  const handler = eventHandlers[event.type] || handleUnknown;
  return handler(state, event);
}

/**
 * Register a custom event handler (for extensibility)
 */
export function registerEventHandler(
  eventType: string,
  handler: EventHandler
): void {
  eventHandlers[eventType] = handler;
}
